import { CheckoutContainer, CustomPreloader } from '@components'
import AdditionalFieldsStep from '@views/CheckoutPayment/AdditionalFieldsStep'
import SelectBankStep from '@views/CheckoutPayment/SelectBankStep'
import WalletCheckout from '@walletPayment/views/WalletCheckout'
import WalletOtp from '@walletPayment/views/WalletOtp'
import {
  CheckoutFlowStep,
  FakeJetonbankBanks,
  isJetonWallet,
  PaymentMethods,
  PaymentSessionStatuses,
  useBanksRequest,
  useCancelPaymentRequest,
  useCheckoutPayment,
  useEventTracker,
  useFilteredBanks,
  usePaymentOptions,
  useSessionId,
  useUnloadBeacon
} from 'mmfintech-checkout-commons'
import type { CheckoutMethodField, SupportedBank } from 'mmfintech-checkout-commons/types'
import {
  isMultiItemArray,
  isValidArray,
  isValidString,
  parseJson,
  tr,
  translatePaymentMethodName
} from 'mmfintech-commons'
import { Input } from 'mmfintech-portal-commons'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { DepositResult } from '../DepositResult'
import FakeLoginPane from './FakeLoginPane'
import { MerchantPane } from './MerchantPane'
import SelectMethodStep from './SelectMethodStep'
import { useFakeBankLogin } from './useFakeBankLogin'

import './checkout.scss'
import { MMButton } from '@components'

const Checkout = () => {
  const sessionId = useSessionId()

  const {
    logo,
    checkoutCountries,
    checkoutPay,
    checkoutPayError,
    checkoutPayFetching,
    session,
    sessionError,
    sessionFetching
  } = useSelector(
    ({
      checkout: {
        logo,
        checkoutCountries,
        checkoutPay,
        checkoutPayError,
        checkoutPayFetching,
        session,
        sessionError,
        sessionFetching
      }
    }) => ({
      logo,
      checkoutCountries,
      checkoutPay,
      checkoutPayError,
      checkoutPayFetching,
      session,
      sessionError,
      sessionFetching
    }),
    shallowEqual
  )

  // const [triggered, setTriggered] = useState<boolean>(false)
  const [eventSubmitted, setEventSubmitted] = useState<boolean>(false)
  const [storageAccess, setStorageAccess] = useState(false)

  const triggeredRef = useRef<boolean>(false)

  const history = useHistory()
  useUnloadBeacon(sessionId)

  const { trackEvent } = useEventTracker()
  const { cancelPayment } = useCancelPaymentRequest()

  const {
    step,
    setStep,
    fieldValues,
    countryCode,
    setCountryCode,
    // hasCountry,
    hasOnlyOneCountry,
    paymentMethod,
    setPaymentMethod,
    selectedPaymentOption,
    handleStartOver,
    handlePay,
    handlePayWithWallet,
    initialized,
    // reloadSession,
    showFieldsAfterBanks,
    jetonBankSpecialCase,
    handlePaymentStatusResponse
  } = useCheckoutPayment({
    preselectSingleMethod: true,
    enableSpecialJetonBankCase: false, // Svilen: 01.04.2026
    enableJetonbankWalletStep: true,
    initialStep: CheckoutFlowStep.SELECT_METHOD
  })

  // const [filteredFields, setFilteredFields] = useState(fields)

  const fakeLogin = useFakeBankLogin()

  const { banksCount } = useBanksRequest()
  const { paymentOptions } = usePaymentOptions()

  const isBankTransfer = useMemo(() => paymentMethod === PaymentMethods.BANKWIRE, [paymentMethod])

  const handleBankSelected = useCallback(
    (bank: SupportedBank): void => {
      const id = bank ? bank.bankChoiceId : ''
      fieldValues.setValue('bankChoiceId', id)
      if (id) {
        void trackEvent(sessionId, 'bank_selected', id)
      }
    },
    [sessionId, fieldValues, trackEvent]
  )

  const filteredBanks = useFilteredBanks(handleBankSelected)

  const hasMethodSelection = useMemo(
    () => isMultiItemArray(checkoutCountries) || isMultiItemArray(paymentOptions),
    [checkoutCountries, paymentOptions]
  )

  const handleCancel = () => {
    if (sessionId) {
      cancelPayment(sessionId)
    }
    void trackEvent(sessionId, 'cancelled_by_payer')
    history.push('/fail')
  }

  const handleSubmit = useCallback(
    (onPay?: () => void) => {
      if (jetonBankSpecialCase && !isJetonWallet(fieldValues.getValue('bankChoiceId'))) {
        fakeLogin.reset()
        setStep(71)
        return
      }

      // if (isBankTransfer) {
      //   if (fieldValues.areValid()) {
      //     setStep(CheckoutFlowStep.BANK_TRANSFER_WARNING1)
      //   }
      //   return
      // }

      onPay?.()
      handlePay()
    },
    [jetonBankSpecialCase, fieldValues, fakeLogin, setStep, handlePay]
  )

  const handleContinueAfterMethodSelection = () => {
    if (
      fieldValues.hasAdditionalFields &&
      paymentMethod !== PaymentMethods.JETON_BANK_WALLET &&
      !showFieldsAfterBanks
    ) {
      setStep(CheckoutFlowStep.ADDITIONAL_FIELDS)
    } else if (fieldValues.hasBankSelection) {
      setStep(CheckoutFlowStep.SELECT_BANK)
    } else if (
      fieldValues.hasAdditionalFields &&
      paymentMethod !== PaymentMethods.JETON_BANK_WALLET &&
      showFieldsAfterBanks
    ) {
      setStep(CheckoutFlowStep.ADDITIONAL_FIELDS)
    } else {
      handleSubmit()
    }
  }

  const handleContinueAfterAdditionalFields = useCallback(() => {
    // if (fieldValues.hasBankSelection && !showFieldsAfterBanks) {
    //   fieldValues.setRequired('bankChoiceId', false)
    // }

    if (fieldValues.areValid()) {
      if (fieldValues.hasBankSelection && !showFieldsAfterBanks) {
        setStep(CheckoutFlowStep.SELECT_BANK)
      } else {
        handleSubmit()
      }
    }
  }, [fieldValues, showFieldsAfterBanks, handleSubmit, setStep])

  const handleBackFromAdditionalFields = useCallback(() => {
    if (showFieldsAfterBanks && fieldValues.hasBankSelection) {
      setStep(CheckoutFlowStep.SELECT_BANK)
    } else {
      setStep(CheckoutFlowStep.SELECT_METHOD)
    }
  }, [fieldValues, showFieldsAfterBanks, setStep])

  const handleSearchFocus = useCallback(() => {
    void trackEvent(sessionId, 'bank_search_focus', null, true)
  }, [sessionId, trackEvent])

  const handleSearchType = useCallback(() => {
    if (!eventSubmitted) {
      void trackEvent(sessionId, 'bank_search_type', null, true)
      setEventSubmitted(true)
    }
  }, [sessionId, trackEvent, eventSubmitted])

  const handleContinueAfterBankSelected = useCallback(() => {
    fieldValues.setRequired('bankChoiceId', true)
    if (showFieldsAfterBanks) {
      setStep(CheckoutFlowStep.ADDITIONAL_FIELDS)
    } else {
      handleSubmit()
    }
  }, [fieldValues, showFieldsAfterBanks, handleSubmit, setStep])

  const handleBackFromBankSelection = useCallback(() => {
    if (!showFieldsAfterBanks && fieldValues.hasAdditionalFields) {
      setStep(CheckoutFlowStep.ADDITIONAL_FIELDS)
    } else {
      setStep(CheckoutFlowStep.SELECT_METHOD)
    }
  }, [fieldValues, showFieldsAfterBanks, setStep])

  const prepareBankOptions = () => {
    const result = []
    result.push({
      value: PaymentMethods.JETON,
      label: tr('METADATA.PAYMENT_OPTIONS.JETON', 'Jeton')
    })

    if (Array.isArray(FakeJetonbankBanks[countryCode])) {
      FakeJetonbankBanks[countryCode].forEach((bank, index) => {
        result.push({
          value: index + 1,
          label: bank.name
        })
      })
    }

    return result
  }

  //todo
  // useEffect(() => {
  //   if (isValidArray(paymentOptions) && isValidArray(fields)) {
  //     const data = {}
  //     if (isJetonWallet(formValues.getValue('bankChoiceId'))) {
  //       const jetonFields = paymentOptions.find((option: any) => isJetonWallet(option.paymentMethod))?.fields
  //       setFilteredFields(jetonFields)
  //       isValidArray(jetonFields) &&
  //         jetonFields.forEach((item: CheckoutMethodField) => {
  //           const { name, prefilledValue: value, required } = item
  //           data[name] = { value, required, ...prepareValidations(item) }
  //         })
  //       formValues.reset(data)
  //       formValues.setValue('bankChoiceId', 'JETON')
  //     } else {
  //       setFilteredFields(selectedPaymentOption?.fields)
  //       const bankChoiceId = formValues.getValue('bankChoiceId')
  //       isValidArray(selectedPaymentOption?.fields) &&
  //         selectedPaymentOption.fields.forEach((item: CheckoutMethodField) => {
  //           if (item.name !== 'bankChoiceId') {
  //             const { name, prefilledValue: value, required } = item
  //             data[name] = { value, required, ...prepareValidations(item) }
  //           }
  //         })
  //       jetonBankSpecialCase && formValues.reset(data)
  //       jetonBankSpecialCase && formValues.setValue('bankChoiceId', bankChoiceId)
  //     }
  //   }
  // }, [formValues.getValue('bankChoiceId'), paymentOptions, fields])

  const prepareResponse = useCallback(
    () =>
      session?.sessionStatus?.value === PaymentSessionStatuses.IN_PROGRESS
        ? {
            result: parseJson(session?.paymentResult, session?.paymentResult),
            resultType: session?.paymentResultType,
            processingAmount: session?.processingAmount,
            processingCurrency: session?.processingCurrency
          }
        : checkoutPay,
    [checkoutPay, session]
  )

  const currencyOptions = useMemo(
    () =>
      typeof selectedPaymentOption === 'object' && isValidArray(selectedPaymentOption?.supportedCurrencies)
        ? selectedPaymentOption.supportedCurrencies
        : [],
    [selectedPaymentOption]
  )

  const validBankSelection = useMemo(() => {
    if (jetonBankSpecialCase) {
      return hasOnlyOneCountry && !FakeJetonbankBanks?.[countryCode || '']
    }

    return (
      !fieldValues.hasBankSelection ||
      (fieldValues.hasBankSelection && banksCount === 1 && isValidString(fieldValues.getValue('bankChoiceId')))
    )
  }, [countryCode, jetonBankSpecialCase, hasOnlyOneCountry, banksCount, fieldValues])

  const validFieldsStep = useMemo(() => {
    if (isValidArray(selectedPaymentOption?.fields)) {
      return !selectedPaymentOption.fields.some(
        (field: CheckoutMethodField) =>
          (!field.prefilledValue || field.displayIfPrefilled) && field.name !== 'bankChoiceId'
      )
    }
    return true
  }, [selectedPaymentOption])

  // biome-ignore lint/correctness/useExhaustiveDependencies: only trigger on paymentMethod change
  useEffect(() => {
    if (paymentMethod && jetonBankSpecialCase) {
      fieldValues.setValue('bankChoiceId', PaymentMethods.JETON)
    }
  }, [paymentMethod])

  const checkAndExecute = () => {
    // console.log('————————————————————————————————————————')
    // console.log('Step:', step, jetonBankSpecialCase)
    // console.log('Initialized:', initialized)
    // console.log('Triggered:', triggeredRef.current)
    // console.log('Payment method:', isValidString(paymentMethod))
    // console.log('Banks:', validBankSelection)
    // console.log('Fields:', validFieldsStep)
    if (
      initialized &&
      !triggeredRef.current &&
      !checkoutPayFetching &&
      isValidString(paymentMethod) &&
      validBankSelection &&
      validFieldsStep
    ) {
      triggeredRef.current = true
      handlePay()
    }
  }

  useEffect(() => {
    if (typeof document.hasStorageAccess === 'function') {
      document.hasStorageAccess().then(response => setStorageAccess(response))
    } else {
      setStorageAccess(true)
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: only trigger if methods, banks and fields are valid
  useEffect(() => {
    checkAndExecute()
  }, [initialized, fieldValues.getValue('bankChoiceId')]) // validBankSelection, validFieldsStep

  // biome-ignore lint/correctness/useExhaustiveDependencies: only trigger if paymentMethod === 'JETON'
  useEffect(() => {
    if (paymentMethod === PaymentMethods.JETON && hasOnlyOneCountry) {
      checkAndExecute()
    }
  }, [paymentMethod])

  return (
    <CheckoutContainer>
      <div className='checkout-content'>
        {sessionFetching || !initialized ? (
          <div className='payment-container'>
            <CustomPreloader />
          </div>
        ) : (
          <>
            {!sessionError && !checkoutPayError && <MerchantPane session={session} logo={logo} />}

            {(step === CheckoutFlowStep.SELECT_METHOD ||
              step === CheckoutFlowStep.SELECT_BANK ||
              step === CheckoutFlowStep.ADDITIONAL_FIELDS) && (
              <div className='payment-container'>
                {step === CheckoutFlowStep.SELECT_METHOD && !triggeredRef.current && (
                  <SelectMethodStep
                    countryCode={countryCode}
                    setCountryCode={setCountryCode}
                    countrySelectionVisible={!hasOnlyOneCountry}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    jetonBankSpecialCase={jetonBankSpecialCase}
                    fieldValues={fieldValues}
                    onContinue={handleContinueAfterMethodSelection}
                    onCancel={handleCancel}
                  />
                )}

                {step === CheckoutFlowStep.ADDITIONAL_FIELDS && (
                  <AdditionalFieldsStep
                    selectedPaymentOption={selectedPaymentOption}
                    paymentMethod={paymentMethod}
                    jetonBankSpecialCase={jetonBankSpecialCase}
                    fieldValues={fieldValues}
                    currencyOptions={currencyOptions}
                    continueCaption={
                      (fieldValues.hasBankSelection && !showFieldsAfterBanks) || isBankTransfer
                        ? tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')
                        : tr('CHECKOUT.PAYMENT.BUTTON_PAY', 'Pay')
                    }
                    onContinue={handleContinueAfterAdditionalFields}
                    onCancel={handleCancel}
                    onBack={
                      hasMethodSelection || (showFieldsAfterBanks && fieldValues.hasBankSelection)
                        ? handleBackFromAdditionalFields
                        : undefined
                    }
                  />
                )}

                {step === CheckoutFlowStep.SELECT_BANK &&
                  (jetonBankSpecialCase ? (
                    <>
                      <div className='payment-method-wrapper'>
                        {tr('CHECKOUT.PAYMENT.PAY_BY', 'Pay with {{METHOD}}', {
                          METHOD: translatePaymentMethodName('INSTANT_BANK_TRANSFER')
                        })}
                      </div>
                      <Input
                        type='select'
                        label={tr('CHECKOUT.PAYMENT.BANK', 'Bank')}
                        {...fieldValues.registerInput('bankChoiceId')}
                        options={prepareBankOptions()}
                        required={true}
                        hideErrorLine
                      />
                      <div className='buttons'>
                        <MMButton
                          type='button'
                          variant='primary'
                          caption={tr('FRONTEND.BUTTONS.CONTINUE', 'Continue')}
                          onClick={handleContinueAfterBankSelected}
                          loading={checkoutPayFetching}
                          data-test='button-continue'
                          fullWidth
                        />
                        {Boolean(session?.showCancelButton) && (
                          <MMButton
                            type='button'
                            variant='secondary'
                            caption={tr('FRONTEND.BUTTONS.CANCEL', 'Cancel')}
                            onClick={handleCancel}
                            disabled={checkoutPayFetching}
                            data-test='button-cancel'
                            fullWidth
                          />
                        )}
                      </div>{' '}
                    </>
                  ) : (
                    <SelectBankStep
                      countryCode={countryCode}
                      paymentMethod={paymentMethod}
                      fieldValues={fieldValues}
                      filteredBanks={filteredBanks}
                      onContinue={handleContinueAfterBankSelected}
                      onCancel={handleCancel}
                      onBack={
                        hasMethodSelection || (!showFieldsAfterBanks && fieldValues.hasAdditionalFields)
                          ? handleBackFromBankSelection
                          : undefined
                      }
                      handleSearchFocus={handleSearchFocus}
                      handleSearchType={handleSearchType}
                    />
                  ))}
              </div>
            )}
          </>
        )}

        {step === 71 && (
          <div className='payment-container'>
            <FakeLoginPane
              countryCode={countryCode}
              backChoiceId={fieldValues.getValue('bankChoiceId')}
              onCancel={() => setStep(CheckoutFlowStep.SELECT_BANK)}
            />
          </div>
        )}

        {step === CheckoutFlowStep.JETONBANK_WALLET && (
          <WalletCheckout
            sessionId={sessionId}
            onCancel={handleStartOver}
            handlePay={handlePayWithWallet}
            hasStorageAccess={storageAccess}
            setStorageAccess={setStorageAccess}
          />
        )}

        {step === CheckoutFlowStep.WALLET_OTP && (
          <WalletOtp onSuccess={response => handlePaymentStatusResponse(response)} wallet={true} />
        )}

        {step === CheckoutFlowStep.DEPOSIT_RESULT && (
          <DepositResult response={prepareResponse()} sessionId={sessionId} onBack={handleStartOver} />
        )}

        <div className='payment-footer'>
          Jeton Bank Limited is licensed and authorised by the Financial Services Unit, Ministry of Finance of the
          Commonwealth of Dominica, licensed as a banking institution under the International Banking Act, fully
          authorised to provide services to clients worldwide, under the prudential supervision of the Financial
          Services Unit. Jeton Bank Limited is registered in the Commonwealth of Dominica, Company Number 2022/C0175,
          with its registered address at 1st Floor, 43 Great George Street, Roseau, Commonwealth of Dominica, Post Code:
          00109-8000. LEI Code: 894500XGIX3R4HCIOC29.
        </div>
      </div>
    </CheckoutContainer>
  )
}

export default Checkout
